import React, { useCallback, useEffect, useRef, useState } from "react";
import { ReactComponent as KebabMenuIcon } from "../../../../../../Assets/ActionIcons/KebabMenuIcon.svg";
import { ReactComponent as CopyButtonIcon } from "../../../../../../Assets/ActionIcons/CopyButtonIcon.svg";
import { ReactComponent as CloseIcon } from "../../../../../../Assets/ActionIcons/CloseIcon.svg";
import { ReactComponent as SpinnerIcon } from "../../../../../../Assets/ActionIcons/SpinnerIcon.svg";
import { PostmarkSenderSignatureView } from "../../../../../../Contracts/Settings";
import { useSettingsStore } from "../../../../../../Store/Settings";
import { useAuth0 } from "@auth0/auth0-react";

const CopiableText: React.FC<{ text: string }> = ({ text }) => {
  const [isCopied, setIsCopied] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (isCopied) {
      setTimeout(() => {
        setIsCopied(false);
      }, 2000);
    }
  }, [isCopied]);
  return (
    <div className="relative flex bg-gray-100 p-2 rounded border border-gray-300">
      <div className="flex justify-between w-full">
        <div ref={textRef} className="overflow-hidden text-ellipsis whitespace-nowrap max-w-60">
          {text}
        </div>
        <CopyButtonIcon
          className="ml-2 w-4 h-4 text-gray-600 hover:text-blue-500 hover:cursor-pointer hover:scale-110 transition-all duration-500"
          onClick={() => {
            navigator.clipboard.writeText(text);
            setIsCopied(true);
          }}
        />
      </div>
      <div className="relative">
        {isCopied && (
          <div className="absolute top-5 -left-8 items-center justify-center bg-gray-950 bg-opacity-50 text-white rounded">
            Copied!
          </div>
        )}
      </div>
    </div>
  );
};

const PostMarkSignatureEmailTile: React.FC<{
  signature: PostmarkSenderSignatureView;
  defaultDetailsOpen: boolean;
}> = ({ signature, defaultDetailsOpen }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isViewDetailsOpen, setIsViewDetailsOpen] = useState(defaultDetailsOpen);
  const menuRef = useRef<HTMLDivElement>(null);
  const { getAccessTokenSilently } = useAuth0();
  const { deletePostmarkSignature, checkPostmarkSignatureStatus } = useSettingsStore();
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [menuRef]);
  useEffect(() => {
    setIsViewDetailsOpen(defaultDetailsOpen);
  }, [defaultDetailsOpen]);

  const deletePostmarkSignatureClicked = useCallback(async () => {
    const userConfirmation = window.confirm(
      "Are you sure you want to delete this domain? Emails sent to your clients will no longer be sent from your email address."
    );
    if (userConfirmation) {
      await deletePostmarkSignature(getAccessTokenSilently, signature.id);
    }
  }, [deletePostmarkSignature, getAccessTokenSilently, signature.id]);

  return (
    <div
      className="flex items-center border border-gray-200 rounded p-2 space-x-4"
      key={signature.id}
    >
      <div>
        <div>{signature.domain_name}</div>
        {signature.sender_email && (
          <div className="text-gray-700">
            {"<"}
            {signature.sender_email}
            {">"}
          </div>
        )}
      </div>
      {isLoading ? (
        <SpinnerIcon className="w-[4.4rem] px-2 h-4 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600" />
      ) : (
        <div
          className={`border rounded w-fit py-0.5 px-2 ${
            signature.dkim_verified && signature.return_path_verified && signature.is_confirmed
              ? "border-lime-300 bg-lime-100 text-lime-600"
              : "border-orange-300 bg-orange-100 text-orange-600"
          }`}
        >
          {signature.dkim_verified && signature.return_path_verified && signature.is_confirmed
            ? "Verified"
            : "Pending"}
        </div>
      )}
      <div className="ml-4 relative">
        <button
          className="text-blue-500"
          disabled={isMenuOpen}
          onClick={() => setIsMenuOpen(!isMenuOpen)}
        >
          <KebabMenuIcon />
        </button>
        {isMenuOpen && (
          <div
            ref={menuRef}
            className="absolute bg-white border border-gray-200 rounded bottom-full left-0 flex flex-col whitespace-nowrap shadow-lg"
          >
            <button
              className={`px-4 py-3 hover:bg-white-smoke`}
              onClick={() => {
                setIsMenuOpen(false);
                setIsViewDetailsOpen(true);
              }}
            >
              View Details
            </button>
            {!(
              signature.is_confirmed &&
              signature.return_path_verified &&
              signature.dkim_verified
            ) && (
              <button
                className={`px-4 py-3 hover:bg-white-smoke ${isLoading && "cursor-progress"}`}
                onClick={async () => {
                  setIsLoading(true);
                  await checkPostmarkSignatureStatus(
                    getAccessTokenSilently,
                    signature.id
                  );
                  setIsLoading(false);
                }}
              >
                Check Status
              </button>
            )}
            <button
              className={`px-4 py-3 border-t border-gray-200 text-red-600 hover:text-red-600 hover:bg-white-smoke`}
              onClick={deletePostmarkSignatureClicked}
            >
              Delete
            </button>
          </div>
        )}
        {isViewDetailsOpen && (
          <div className="z-40">
            <div className="fixed top-0 left-0 flex items-center justify-center w-screen h-[44rem] opacity-100 z-20">
              <div className="bg-white flex flex-col rounded shadow-lg max-w-[48rem]">
                <div className="p-4 flex justify-between border-b border-gray-200 font-semibold text-lg">
                  <div>Domain Authentication</div>
                  <button
                    className="hover:scale-110 transition-transform duration-500"
                    onClick={() => {
                      setIsViewDetailsOpen(false);
                    }}
                  >
                    <CloseIcon className="w-3 h-3" />
                  </button>
                </div>
                <div className="p-4">
                  Add the following DKIM and Return-Path DNS records in your domain to verify your
                  domain and ensure effective email delivery
                </div>

                <div className="px-4 py-1">
                  We will notify you as soon as we've verified these records from our end.
                </div>
                <table className="m-8">
                  <thead className="border-t border-b border-gray-200">
                    <tr>
                      <th className="p-2">Record Type</th>
                      <th className="p-2">Hostname</th>
                      <th className="p-2">Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="p-2 text-center">TXT</td>
                      <td className="p-2">
                        <CopiableText text={signature.dkim_pending_host} />
                      </td>
                      <td className="p-2">
                        <CopiableText text={signature.dkim_pending_text_value} />
                      </td>
                    </tr>
                    <tr>
                      <td className="p-2 text-center">CNAME</td>
                      <td className="p-2">
                        <CopiableText text={signature.return_path} />
                      </td>
                      <td className="p-2">
                        <CopiableText text="pm.mtasv.net" />
                      </td>
                    </tr>
                  </tbody>
                </table>
                <div className="flex justify-end border-t border-gray-200">
                  <button
                    className="bg-blue-600 text-white font-semibold rounded px-4 py-2 m-4 hover:bg-blue-500 transition-colors duration-500"
                    onClick={() => {
                      setIsViewDetailsOpen(false);
                    }}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
            <div className="fixed top-0 left-0 w-screen h-screen z-10 flex items-center justify-center bg-gray-950 opacity-20" />
          </div>
        )}
      </div>
    </div>
  );
};

export default PostMarkSignatureEmailTile;
